import React, { useState, useMemo } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Typography, useMediaQuery } from '@mui/material';

import { HORIZONTAL_MAX_ITEM, MenuOrientation } from 'config';

import useConfig from 'hooks/useConfig';
import { connect } from 'react-redux';

// project import
import NavGroup from './NavGroup';
import menuItem from 'menu-items';
import { guestLikeRoles, userRoles, userTypes } from 'utils/constants';
import { useResiAccess } from 'hooks/resi/useResiAccess';
import { filterMenuByPermissions } from 'permissions/filterMenuByPermissions';
import { getAccessibleMenuGroups } from 'utils/accessible-menu';

// ==============================|| DRAWER CONTENT - NAVIGATION ||============================== //

const Navigation = ({ drawerOpen, activeOrg, activePermissions, userData }) => {
    const theme = useTheme();

    const downLG = useMediaQuery(theme.breakpoints.down('lg'));

    const { menuOrientation } = useConfig();
    const [selectedItems, setSelectedItems] = useState('');
    const [selectedLevel, setSelectedLevel] = useState(0);
    const { can } = useResiAccess();
    const resiDashboardMenu = useMemo(() => {
        return filterMenuByPermissions(menuItem?.resiItems ?? [], can);
    }, [can]);
    const filteredMenuItems = useMemo(() => {
        if (!menuItem) return { items: [] };

        if (
            userData?.role === userRoles.RESIDENT ||
            userData?.role === userRoles.TENANT ||
            guestLikeRoles.includes(userData?.role)
        ) {
            return {
                items: resiDashboardMenu
            };
        }

        if (userData?.type === userTypes.SUPER_ADMIN) {
            return { items: menuItem.items.filter(item => item.id === 'super_admin') };
        }

        if (activeOrg && menuItem && activePermissions?.length > 0) {
            const filteredItems = getAccessibleMenuGroups({
                menuGroups: menuItem.items,
                activeOrg,
                permissions: activePermissions,
                userData
            });

            return { items: filteredItems };
        }

        return { items: [] };
    }, [userData, activeOrg, activePermissions, resiDashboardMenu]);

    const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

    const lastItem = isHorizontal ? HORIZONTAL_MAX_ITEM : null;
    let lastItemIndex = filteredMenuItems.items.length - 1;
    let remItems = [];
    let lastItemId;

    //  first it checks menu item is more than giving HORIZONTAL_MAX_ITEM after that get lastItemid by giving horizontal max
    // item and it sets horizontal menu by giving horizontal max item lastly slice menuItem from array and set into remItems

    if (lastItem && lastItem < filteredMenuItems.items.length) {
        lastItemId = filteredMenuItems.items[lastItem - 1].id;
        lastItemIndex = lastItem - 1;
        remItems = filteredMenuItems.items
            .slice(lastItem - 1, filteredMenuItems.items.length)
            .map(item => ({
                title: item.title,
                elements: item.children,
                icon: item.icon
            }));
    }

    const navGroups = filteredMenuItems.items.slice(0, lastItemIndex + 1).map(item => {
        switch (item.type) {
            case 'group':
                return (
                    <NavGroup
                        key={item.id}
                        setSelectedItems={setSelectedItems}
                        setSelectedLevel={setSelectedLevel}
                        selectedLevel={selectedLevel}
                        selectedItems={selectedItems}
                        lastItem={lastItem}
                        remItems={remItems}
                        lastItemId={lastItemId}
                        item={item}
                    />
                );
            default:
                return (
                    <Typography key={item.id} variant="h6" color="error" align="center">
                        Fix - Navigation Group
                    </Typography>
                );
        }
    });
    return (
        <Box
            sx={{
                pt: drawerOpen ? (isHorizontal ? 0 : 2) : 0,
                '& > ul:first-of-type': { mt: 0 },
                display: isHorizontal ? { xs: 'block', lg: 'flex' } : 'block'
            }}
        >
            {navGroups}
        </Box>
    );
};

const mapStateToProps = ({ menu, user }) => {
    const { drawerOpen } = menu;
    const { activeOrg, userData, activePermissions } = user;

    return { drawerOpen, activeOrg, userData, activePermissions };
};

export default connect(mapStateToProps, {})(Navigation);
